import React from "react";
import PropTypes from "prop-types";
import { STATUS_META, PRIORITY_META, relativeTime } from "../supportConstants";
import styles from "./TicketCard.module.css";

/**
 * Card de um chamado na lista (painel esquerdo).
 * - Badge de status humanizado + borda lateral colorida (cor do status).
 * - Preview da última mensagem (1 linha) ou da descrição como fallback.
 * - Timestamp relativo da última atividade.
 * - Badge de prioridade (apenas LOW/HIGH/URGENT; NORMAL não exibe).
 */
export default function TicketCard({ ticket, selected, onSelect }) {
  const status = STATUS_META[ticket.status] ?? STATUS_META.OPEN;
  const priority = PRIORITY_META[ticket.priority];

  const lastMessage = ticket.messages?.length
    ? ticket.messages[ticket.messages.length - 1]
    : null;
  const preview = lastMessage?.content ?? ticket.description ?? "";
  const previewPrefix = lastMessage?.isStaff ? "Suporte Nexus: " : "";
  const lastActivity = lastMessage?.createdAt ?? ticket.updatedAt ?? ticket.createdAt;

  return (
    <button
      type="button"
      className={`${styles.card} ${selected ? styles.selected : ""}`}
      style={{ borderLeftColor: status.color }}
      onClick={() => onSelect(ticket)}
      aria-pressed={selected}
      aria-label={`Chamado: ${ticket.subject}`}
    >
      <div className={styles.topRow}>
        <span
          className={styles.statusBadge}
          style={{ background: status.soft, color: status.color }}
        >
          <span aria-hidden="true">{status.emoji}</span> {status.label}
        </span>
        {priority?.show && (
          <span
            className={styles.priorityBadge}
            style={{ background: priority.soft, color: priority.color }}
          >
            {priority.label}
          </span>
        )}
      </div>
      <h3 className={styles.subject}>{ticket.subject}</h3>
      {preview && (
        <p className={styles.preview}>
          {previewPrefix}
          {preview}
        </p>
      )}
      {lastActivity && (
        <span className={styles.time}>{relativeTime(lastActivity)}</span>
      )}
    </button>
  );
}

TicketCard.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.string,
    subject: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    priority: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    messages: PropTypes.array,
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};
